import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useAttendanceExceptionsApiService,
  useResolveExceptionApiService,
  useReopenExceptionApiService,
  useExceptionCountApiService,
} from "../services/api/AttendanceExceptionsApiService";
import { useShiftLocationsApiService } from "../services/api/ShiftLocationsApiService";
import { AttendanceException } from "../services/model/AttendanceException";
import { NewExceptionType } from "../constants/exceptionTypeConfig";
import { ResolveExceptionRequest } from "../services/request/AttendanceExceptionsRequest";

const TYPE_FILTER_VALUES: ReadonlyArray<"all" | NewExceptionType> = [
  "all",
  "no_show",
  "supervisor_clock",
  "no_data",
  "geofence",
];

const parseTypeFilter = (value: string | null): "all" | NewExceptionType => {
  if (!value) return "all";
  return (TYPE_FILTER_VALUES as ReadonlyArray<string>).includes(value)
    ? (value as "all" | NewExceptionType)
    : "all";
};

const parseDateFilter = (value: string | null): Date | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.toDate() : undefined;
};

const extractResults = (
  payload:
    | { results?: AttendanceException[] }
    | AttendanceException[]
    | undefined
): AttendanceException[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  return payload.results ?? [];
};

export interface SiteOption {
  id: string;
  name: string;
}

export interface UseExceptionsResult {
  exceptions: AttendanceException[];
  filteredUnresolved: AttendanceException[];
  resolved: AttendanceException[];
  unresolvedCount: number;
  countByType: (type: NewExceptionType) => number;
  siteOptions: SiteOption[];

  typeFilter: "all" | NewExceptionType;
  setTypeFilter: (val: "all" | NewExceptionType) => void;
  siteFilter: string;
  setSiteFilter: (val: string) => void;
  dateFilter: Date | undefined;
  setDateFilter: (val: Date | undefined) => void;
  searchQuery: string;
  setSearchQuery: (val: string) => void;

  selectedException: AttendanceException | null;
  setSelectedException: (exception: AttendanceException | null) => void;

  handleResolve: (id: string, body?: ResolveExceptionRequest) => void;
  handleReopen: (id: string) => void;

  isLoading: boolean;
  error: unknown;
}

const useExceptions = (
  siteId?: string,
  shiftSiteId?: string
): UseExceptionsResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [typeFilter, setTypeFilterState] = useState<"all" | NewExceptionType>(
    () => parseTypeFilter(searchParams.get("type"))
  );
  const [siteFilter, setSiteFilterState] = useState<string>(
    () => searchParams.get("site_id") || "all"
  );
  const [dateFilter, setDateFilterState] = useState<Date | undefined>(() =>
    parseDateFilter(searchParams.get("date"))
  );
  const [searchQuery, setSearchQueryState] = useState<string>(
    () => searchParams.get("q") || ""
  );

  const [selectedException, setSelectedException] =
    useState<AttendanceException | null>(null);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const setTypeFilter = (val: "all" | NewExceptionType): void => {
    setTypeFilterState(val);
    updateSearchParams({ type: val === "all" ? null : val });
  };

  const setSiteFilter = (val: string): void => {
    setSiteFilterState(val);
    updateSearchParams({ site_id: val === "all" ? null : val });
  };

  const setDateFilter = (val: Date | undefined): void => {
    setDateFilterState(val);
    updateSearchParams({
      date: val ? dayjs(val).format("YYYY-MM-DD") : null,
    });
  };

  const setSearchQuery = (val: string): void => {
    setSearchQueryState(val);
    updateSearchParams({ q: val || null });
  };

  const effectiveSiteFilter =
    siteId || (siteFilter !== "all" ? siteFilter : undefined);
  const dateParam = dateFilter
    ? dayjs(dateFilter).format("YYYY-MM-DD")
    : undefined;
  const typeParam = typeFilter !== "all" ? typeFilter : undefined;
  const searchParam = searchQuery || undefined;

  // Single-page fetch sized to the realistic per-tenant ceiling (no per-day/site
  // tenant has been observed near 1k). When this proves insufficient we should
  // switch to true server-side pagination — for now the count API below
  // provides the authoritative total when the list is truncated.
  const LIST_PAGE_SIZE = 1000;

  const unresolvedQuery = useAttendanceExceptionsApiService({
    status: "open",
    shift_location: effectiveSiteFilter,
    shift_site: shiftSiteId,
    exception_type: typeParam,
    date: dateParam,
    search: searchParam,
    include_supervisor_clock: true,
    page_size: LIST_PAGE_SIZE,
  });

  const resolvedQuery = useAttendanceExceptionsApiService({
    status: "resolved",
    shift_location: effectiveSiteFilter,
    shift_site: shiftSiteId,
    exception_type: typeParam,
    date: dateParam,
    search: searchParam,
    include_supervisor_clock: false,
    page_size: LIST_PAGE_SIZE,
  });

  const unresolvedCountQuery = useExceptionCountApiService({
    status: "open",
    shift_location: effectiveSiteFilter,
    shift_site: shiftSiteId,
    include_supervisor_clock: true,
  });

  const filteredUnresolved = useMemo(
    () => extractResults(unresolvedQuery.data),
    [unresolvedQuery.data]
  );

  const resolved = useMemo(
    () => extractResults(resolvedQuery.data),
    [resolvedQuery.data]
  );

  // Prefer the authoritative count from the count endpoint when available; fall
  // back to the in-memory list length while it's still loading (e.g. first paint).
  const unresolvedCount =
    unresolvedCountQuery.data?.count ?? filteredUnresolved.length;

  const countByType = useCallback(
    (type: NewExceptionType) =>
      filteredUnresolved.filter((e) => e.exception_type === type).length,
    [filteredUnresolved]
  );

  const shiftLocationsQuery = useShiftLocationsApiService({});
  const siteOptions = useMemo<SiteOption[]>(() => {
    const locations = shiftLocationsQuery.data?.results ?? [];
    return locations
      .map((loc) => ({ id: loc.id, name: loc.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [shiftLocationsQuery.data]);

  const exceptions = useMemo(
    () => [...filteredUnresolved, ...resolved],
    [filteredUnresolved, resolved]
  );

  const resolveMutation = useResolveExceptionApiService();
  const reopenMutation = useReopenExceptionApiService();

  const handleResolve = useCallback(
    (id: string, body?: ResolveExceptionRequest) => {
      resolveMutation.mutate({ id, body: body ?? {} });
    },
    [resolveMutation]
  );

  const handleReopen = useCallback(
    (id: string) => {
      reopenMutation.mutate(id);
    },
    [reopenMutation]
  );

  return {
    exceptions,
    filteredUnresolved,
    resolved,
    unresolvedCount,
    countByType,
    siteOptions,
    typeFilter,
    setTypeFilter,
    siteFilter,
    setSiteFilter,
    dateFilter,
    setDateFilter,
    searchQuery,
    setSearchQuery,
    selectedException,
    setSelectedException,
    handleResolve,
    handleReopen,
    isLoading: unresolvedQuery.isLoading || resolvedQuery.isLoading,
    error: unresolvedQuery.error || resolvedQuery.error,
  };
};

export default useExceptions;
